<template>
  <div class="system-log-container">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <h1 class="page-title">System Change Logs</h1>
          <p class="page-subtitle">Monitor system changes and audit trails across all tenants</p>
        </div>
        <div class="header-right">
          <button class="btn btn-refresh-header" @click="loadData" :disabled="page.isLoading">
            <i class="fas fa-sync-alt me-2" :class="{ 'fa-spin': page.isLoading }"></i>
            Refresh Logs
          </button>
        </div>
      </div>
    </div>

    <!-- Main Card Container -->
    <div class="management-card">
      <!-- Filter Bar -->
      <div class="filter-bar">
        <div class="filter-left">
          <!-- Search Control -->
          <div class="search-box-custom">
            <div class="search-type-tabs">
              <button 
                v-for="option in searchOptions" 
                :key="option.value"
                class="tab-btn"
                :class="{ active: searchBy.selected === option.value }"
                @click="selectSearchType(option.value)"
              >
                {{ option.text }}
              </button>
            </div>
            <div class="search-input-wrapper">
              <input 
                type="text" 
                class="form-control-search" 
                v-model="searchBy.criteria" 
                placeholder="Search..." 
                @keyup.enter="applyFilters" 
              />
              <button class="btn-search" @click="applyFilters">
                <i class="fas fa-search"></i>
              </button>
            </div>
          </div>

          <!-- Date Range Control -->
          <div class="date-filter">
            <div class="section-label-tabs">
              <span class="label-tab">Date Range</span>
            </div>
            <div class="date-input-group">
              <div class="date-input-wrapper">
                <input v-model="filters.date_from" type="date" class="date-input" />
                <span class="date-separator">to</span>
                <input v-model="filters.date_to" type="date" class="date-input" />
              </div>
              <button class="btn-filter-apply" @click="applyFilters" title="Apply Date Filter">
                <i class="fas fa-filter"></i>
              </button>
              <button v-if="filters.date_from || filters.date_to" class="btn-filter-clear" @click="clearDateFilters" title="Clear Date Range">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
        </div>
        
        <div class="filter-right">
          <div class="stats-summary">
            <span class="stats-label">Total Results:</span>
            <span class="stats-value">{{ meta.total_count || 0 }}</span>
          </div>
          <button class="btn-reset-primary" @click="clearFilters" title="Reset All Filters">
            <i class="fas fa-undo-alt"></i>
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <div class="card-content">
        <!-- Loading State -->
        <div v-if="page.isLoading && !page.output" class="loading-state">
          <div class="loader-box">
            <div class="spinner-border text-danger" role="status"></div>
            <p>Fetching logs...</p>
          </div>
        </div>

        <!-- Table Content -->
        <div v-else-if="page.output" class="table-outer">
          <div class="table-responsive">
            <table class="modern-list-table">
              <thead>
                <tr>
                  <th width="60" class="col-no">No</th>
                  <th width="180">
                    <div class="sortable-header" @click="handleSort('created_at')">
                      Timestamp
                      <i class="fas fa-sort ms-1" :class="getSortIcon('created_at')"></i>
                    </div>
                  </th>
                  <th width="200">User Identity</th>
                  <th width="150">Tenant Scope</th>
                  <th width="180">Component</th>
                  <th width="120">Action Type</th>
                  <th class="text-end pe-4" width="100">Details</th>
                </tr>
              </thead>
              <tbody>
                <template v-if="systemLogs.length === 0">
                  <tr>
                    <td colspan="7">
                      <div class="empty-list">
                        <div class="empty-art">
                          <i class="fas fa-search"></i>
                        </div>
                        <h3>No Logs Found</h3>
                        <p>Try adjusting your filters or date range to find what you're looking for.</p>
                        <button class="btn-clear-empty mt-3" @click="clearFilters">
                          <i class="fas fa-eraser me-2"></i>
                          Clear All Filters
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>

                <template v-else v-for="(log, index) in systemLogs" :key="log.id || index">
                  <tr 
                    :class="{'expanded': expandedRows.includes(index)}"
                    @click="toggleRow(index)"
                    class="clickable-row"
                  >
                    <td class="col-no" @click.stop>{{ (meta.page - 1) * meta.limit + index + 1 }}</td>
                    <td>
                      <div class="timestamp-box">
                        <span class="date">{{ formatDate(log.created_at) }}</span>
                        <span class="time">{{ formatTime(log.created_at) }}</span>
                      </div>
                    </td>
                    <td>
                      <div class="user-info-box">
                        <div class="user-avatar">
                          {{ (log.user_name || 'U').charAt(0).toUpperCase() }}
                        </div>
                        <div class="user-details">
                          <span class="user-name">{{ log.user_name || 'Unknown User' }}</span>
                          <div class="user-id-wrapper">
                            <span class="user-id">{{ showUserId[index] ? log.user_id : shortenUserId(log.user_id) }}</span>
                            <button @click.stop="toggleUserId(index)" class="btn-toggle-id">
                              <i class="fas" :class="showUserId[index] ? 'fa-eye-slash' : 'fa-eye'"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span class="scope-pill">
                        <i class="fas fa-building me-1"></i>
                        {{ log.tenant || 'System' }}
                      </span>
                    </td>
                    <td>
                      <span class="component-pill">
                        {{ log.component }}
                      </span>
                    </td>
                    <td>
                      <span class="action-pill" :class="log.type">
                        <span class="status-dot"></span>
                        {{ log.type ? log.type.toUpperCase() : 'UNKNOWN' }}
                      </span>
                    </td>
                    <td class="text-end pe-4">
                      <button 
                        class="action-btn" 
                        :class="{ active: expandedRows.includes(index) }"
                        @click.stop="toggleRow(index)"
                        title="Toggle Details"
                      >
                        <i class="fas" :class="expandedRows.includes(index) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                      </button>
                    </td>
                  </tr>

                  <!-- Expanded Detail Row -->
                  <tr v-if="expandedRows.includes(index)" class="detail-row">
                    <td colspan="7" class="detail-container">
                      <div class="detail-content">
                        <div class="row g-4">
                          <!-- Before State -->
                          <div class="col-md-6">
                            <div class="state-card" :class="{ 'no-data': !log.before }">
                              <div class="state-header before">
                                <i class="fas fa-history me-2"></i>
                                Previous State
                              </div>
                              <div v-if="log.before" class="state-body custom-json-pretty">
                                <vue-json-pretty 
                                  :data="parseJsonSafely(log.before)" 
                                  :deep="1" 
                                  :showDoubleQuotes="true"
                                  :showLength="true"
                                  :showLine="true"
                                  :theme="'dark'"
                                />
                              </div>
                              <div v-else class="state-body empty">
                                <div class="empty-placeholder">
                                  <i class="fas fa-plus-circle mb-2"></i>
                                  <span>New Record Created</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <!-- After State -->
                          <div class="col-md-6">
                            <div class="state-card" :class="{ 'no-data': !log.after }">
                              <div class="state-header after">
                                <i class="fas fa-check-circle me-2"></i>
                                New State
                              </div>
                              <div v-if="log.after" class="state-body custom-json-pretty">
                                <vue-json-pretty 
                                  :data="parseJsonSafely(log.after)" 
                                  :deep="1" 
                                  :showDoubleQuotes="true"
                                  :showLength="true"
                                  :showLine="true"
                                  :theme="'dark'"
                                />
                              </div>
                              <div v-else class="state-body empty">
                                <div class="empty-placeholder">
                                  <i class="fas fa-trash-alt mb-2"></i>
                                  <span>Record Deleted</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Footer / Pagination -->
      <div class="card-footer" v-if="page.output && systemLogs.length > 0">
        <o-pagination 
          :page="meta.page"  
          :total="meta.total" 
          @callback="navPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { OPagination } from '@/components/OmnihControl'
import { reactive, ref, onMounted, onBeforeUnmount } from 'vue'
import VueJsonPretty from "vue-json-pretty"
import "vue-json-pretty/lib/styles.css"
import api from '@/helpers/api'

export default {
  name: 'ExtranetSystemLog',
  
  components: {
    OPagination,
    VueJsonPretty
  },

  setup() {
    const isMounted = ref(true)
    const expandedRows = ref([])
    const showUserId = reactive({})
    
    const page = reactive({
      isLoading: false,
      isError: false,
      errorMessage: '',
      output: false
    })

    const systemLogs = ref([])

    const meta = reactive({
      page: 1,
      limit: 15,
      total: 1,
      total_count: 0,
      sortBy: 'created_at',
      sortDir: 'desc'
    })

    const searchOptions = [
        { value: 'component', text: 'Component' },
        { value: 'user_name', text: 'User Name' },
        { value: 'user_id', text: 'User ID' }
    ]

    const searchBy = reactive({
        selected: 'component',
        criteria: ''
    })

    const filters = reactive({
      date_from: '',
      date_to: ''
    })

    const loadData = async () => {
      if (!isMounted.value) return
      
      page.isLoading = true
      page.isError = false
      expandedRows.value = []

      try {
        const params = {
          custom: {
            page: meta.page,
            per_page: meta.limit,
            sort_by: meta.sortBy,
            sort_dir: meta.sortDir
          }
        }

        if (searchBy.criteria) {
          params.custom.search = searchBy.criteria
          params.custom.search_by = searchBy.selected
        }

        if (filters.date_from) params.custom.date_from = filters.date_from
        if (filters.date_to) params.custom.date_to = filters.date_to

        Object.keys(params.custom).forEach(key => {
          if (params.custom[key] === null || params.custom[key] === '') delete params.custom[key]
        })

        const response = await api.request('get', '/api/extranet/system-logs', params)

        if (!isMounted.value) return

        if (response.data && response.data.data) {
          const data = response.data.data
          systemLogs.value = data.data_res || []
          
          if (data.meta) {
            meta.page = parseInt(data.meta.page) || 1
            meta.limit = parseInt(data.meta.limit) || 15
            meta.total = parseInt(data.meta.total) || 1
            meta.total_count = data.meta.total_count || 0
          }
        }
        page.output = true
      } catch (error) {
        if (!isMounted.value) return
        page.isError = true
        page.errorMessage = error.response?.data?.message || 'Failed to load system logs'
      } finally {
        if (isMounted.value) {
          page.isLoading = false
        }
      }
    }

    function navPage(pageNumber) {
      if (pageNumber === meta.page) return
      meta.page = pageNumber
      loadData()
    }

    function applyFilters() {
      meta.page = 1
      loadData()
    }

    function clearFilters() {
      searchBy.criteria = ''
      filters.date_from = ''
      filters.date_to = ''
      meta.page = 1
      loadData()
    }

    function clearDateFilters() {
      filters.date_from = ''
      filters.date_to = ''
      applyFilters()
    }
    
    function selectSearchType(type) {
      searchBy.selected = type
      if (searchBy.criteria) applyFilters()
    }

    function handleSort(field) {
      if (meta.sortBy === field) {
        meta.sortDir = meta.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        meta.sortBy = field
        meta.sortDir = 'desc'
      }
      loadData()
    }

    function toggleRow(index) {
      const pos = expandedRows.value.indexOf(index)
      if (pos > -1) {
        expandedRows.value.splice(pos, 1)
      } else {
        expandedRows.value.push(index)
      }
    }

    function toggleUserId(index) {
      showUserId[index] = !showUserId[index]
    }

    function formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric'
      })
    }

    function formatTime(dateString) {
      if (!dateString) return ''
      return new Date(dateString).toLocaleTimeString('en-GB', {
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      })
    }

    function shortenUserId(id) {
      if (!id) return '-'
      if (id.length <= 10) return id
      return id.substring(0, 8) + '...'
    }

    function formatJson(json) {
      try {
        if (typeof json === 'string') {
          return JSON.stringify(JSON.parse(json), null, 2)
        }
        return JSON.stringify(json, null, 2)
      } catch (e) {
        return json || '-'
      }
    }

    /**
     * Parse JSON safely for vue-json-pretty
     */
    function parseJsonSafely(json) {
      try {
        if (typeof json === 'string') {
          return JSON.parse(json)
        }
        return json
      } catch (e) {
        return { error: 'Invalid JSON format', raw: json }
      }
    }

    function getActionBadgeClass(type) {
      const map = {
        create: 'create',
        update: 'update',
        delete: 'delete'
      }
      return map[type] || ''
    }

    function getSortIcon(field) {
      if (meta.sortBy !== field) return 'text-muted opacity-25'
      return meta.sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
    }

    onMounted(() => {
        loadData()
    })

    onBeforeUnmount(() => {
        isMounted.value = false
    })

    return {
      page,
      meta,
      filters,
      systemLogs,
      expandedRows,
      searchBy,
      searchOptions,
      showUserId,
      loadData,
      navPage,
      applyFilters,
      clearFilters,
      clearDateFilters,
      selectSearchType,
      handleSort,
      toggleRow,
      toggleUserId,
      formatDate,
      formatTime,
      shortenUserId,
      formatJson,
      parseJsonSafely,
      getActionBadgeClass,
      getSortIcon
    }
  }
}
</script>

<style scoped>
.system-log-container {
    max-width: 1400px;
    margin: 0 auto;
}

/* Page Header */
.page-header {
    margin-bottom: 32px;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.page-title {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 4px;
}

.page-subtitle {
    font-size: 14px;
    color: #64748b;
    margin: 0;
}

/* Management Card */
.management-card {
    background: white;
    border-radius: var(--extranet-rounded);
    border: 1px solid var(--extranet-accent);
    overflow: hidden;
}

/* Filter Bar */
.filter-bar {
    padding: 24px 32px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    background: #ffffff;
    border-bottom: 1px solid #f1f5f9;
    gap: 24px;
}

.filter-left {
    display: flex;
    align-items: flex-end;
    gap: 32px;
    flex: 1;
}

.search-box-custom {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 0 1 400px;
}

.date-filter {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.section-label-tabs {
    display: flex;
}

.label-tab {
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.search-type-tabs {
    display: flex;
    gap: 4px;
    background: #f1f5f9;
    padding: 4px;
    border-radius: var(--extranet-rounded);
    width: fit-content;
}

.tab-btn {
    border: none;
    background: transparent;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
    border-radius: var(--extranet-rounded);
    transition: all 0.2s;
    cursor: pointer;
}

.tab-btn.active {
    background: white;
    color: var(--extranet-primary);
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.search-input-wrapper {
    display: flex;
    align-items: center;
    background: #f8fafc;
    border: 1px solid var(--extranet-accent);
    border-radius: var(--extranet-rounded);
    padding: 4px;
    transition: all 0.2s;
}

.search-input-wrapper:focus-within {
    border-color: var(--extranet-primary);
    background: white;
    box-shadow: 0 0 0 3px rgba(181, 26, 31, 0.05);
}

.form-control-search {
    border: none;
    background: transparent;
    padding: 8px 12px;
    font-size: 14px;
    color: #1e293b;
    width: 100%;
    outline: none;
}

.btn-search {
    background: var(--extranet-primary);
    border: 1px solid var(--extranet-primary);
    width: 36px;
    height: 36px;
    border-radius: var(--extranet-rounded);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-search:hover {
    filter: brightness(0.9);
    transform: translateY(-1px);
}

/* Date Input Group */
.date-input-group {
    display: flex;
    align-items: center;
    gap: 8px;
}

.date-input-wrapper {
    display: flex;
    align-items: center;
    background: #f8fafc;
    border: 1px solid var(--extranet-accent);
    border-radius: var(--extranet-rounded);
    padding: 6px 12px;
    gap: 12px;
}

.date-input {
    border: none;
    background: transparent;
    font-size: 13px;
    color: #1e293b;
    outline: none;
    cursor: pointer;
}

.date-separator {
    font-size: 11px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
}

.btn-filter-apply, .btn-filter-clear, .btn-reset-primary, .btn-refresh-header, .btn-clear-empty {
    height: 38px;
    border: none;
    background: var(--extranet-primary);
    border-radius: var(--extranet-rounded);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    font-weight: 600;
}

.btn-filter-apply {
    width: 38px;
}

.btn-filter-apply:hover, .btn-reset-primary:hover, .btn-refresh-header:hover, .btn-clear-empty:hover {
    filter: brightness(0.9);
    transform: translateY(-1px);
}

.btn-reset-primary {
    width: 38px;
}

.btn-refresh-header {
    padding: 0 20px;
    font-size: 14px;
}

.btn-clear-empty {
    padding: 0 24px;
    margin: 0 auto;
}

.btn-filter-clear {
    width: 38px;
    height: 38px;
    border: 1px solid var(--extranet-accent);
    background: white;
    border-radius: var(--extranet-rounded);
    color: #ef4444;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-filter-clear:hover {
    border-color: #fecaca;
    background: #fef2f2;
}

.filter-right {
    display: flex;
    align-items: center;
    gap: 16px;
}

.stats-summary {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: #f8fafc;
    border-radius: var(--extranet-rounded);
    font-size: 13px;
    border: 1px solid var(--extranet-accent);
}

.stats-label { color: #64748b; font-weight: 500; }
.stats-value { color: #0f172a; font-weight: 700; }

/* Table Area */
.modern-list-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.modern-list-table thead th {
    padding: 16px 24px;
    background: #f8fafc;
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    border-bottom: 1px solid var(--extranet-accent);
    text-align: left;
}

.sortable-header {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    transition: color 0.2s;
}

.sortable-header:hover {
    color: var(--extranet-primary);
}

.modern-list-table tbody td {
    padding: 16px 24px;
    vertical-align: middle;
    border-bottom: 1px solid var(--extranet-accent);
    color: #334155;
    font-size: 14px;
}

.modern-list-table tbody tr.clickable-row {
    cursor: pointer;
    transition: all 0.2s;
}

.modern-list-table tbody tr.clickable-row:hover {
    background: #fffafa;
}

.modern-list-table tbody tr.expanded {
    background: #fff8f8;
}

.col-no {
    color: #94a3b8 !important;
    font-weight: 500;
    text-align: center;
}

/* User Info Box */
.user-info-box {
    display: flex;
    align-items: center;
    gap: 12px;
}

.user-avatar {
    width: 36px;
    height: 36px;
    background: #f1f5f9;
    border-radius: var(--extranet-rounded);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    color: #64748b;
    font-size: 14px;
    border: 1px solid var(--extranet-accent);
}

.user-details {
    display: flex;
    flex-direction: column;
}

.user-name {
    font-weight: 600;
    color: #1e293b;
    font-size: 14px;
}

.user-id-wrapper {
    display: flex;
    align-items: center;
    gap: 6px;
}

.user-id {
    font-size: 12px;
    color: #94a3b8;
    font-family: 'JetBrains Mono', monospace;
}

.btn-toggle-id {
    border: none;
    background: transparent;
    padding: 0;
    color: #cbd5e1;
    font-size: 10px;
    cursor: pointer;
    transition: color 0.2s;
}

.btn-toggle-id:hover {
    color: var(--extranet-primary);
}

/* Pills */
.scope-pill {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    background: #f1f5f9;
    color: #475569;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
}

.component-pill {
    display: inline-flex;
    padding: 4px 12px;
    background: #fff5f5;
    color: var(--extranet-primary);
    border-radius: var(--extranet-rounded);
    font-size: 12px;
    font-weight: 600;
    border: 1px solid #fee2e2;
}

.action-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    background: #f8fafc;
    color: #64748b;
    border: 1px solid var(--extranet-accent);
}

.status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #cbd5e1;
}

.action-pill.create { background: #ecfdf5; color: #059669; border-color: #a7f3d0; }
.action-pill.create .status-dot { background: #059669; }

.action-pill.update { background: #fffbeb; color: #d97706; border-color: #fde68a; }
.action-pill.update .status-dot { background: #d97706; }

.action-pill.delete { background: #fef2f2; color: #dc2626; border-color: #fecaca; }
.action-pill.delete .status-dot { background: #dc2626; }

/* Action Button */
.action-btn {
    width: 36px;
    height: 36px;
    border-radius: var(--extranet-rounded);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--extranet-accent);
    background: white;
    color: #64748b;
    transition: all 0.2s;
    cursor: pointer;
}

.action-btn:hover, .action-btn.active {
    background: #1e293b;
    color: white;
    border-color: #1e293b;
}

/* Timestamp */
.timestamp-box {
    display: flex;
    flex-direction: column;
}

.timestamp-box .date {
    font-weight: 600;
    color: #1e293b;
}

.timestamp-box .time {
    font-size: 12px;
    color: #94a3b8;
}

/* Details Area */
.detail-container {
    padding: 0 !important;
}

.detail-content {
    padding: 32px;
    background: #f8fafc;
    border-bottom: 2px solid var(--extranet-accent);
}

.state-card {
    background: white;
    border-radius: var(--extranet-rounded);
    border: 1px solid var(--extranet-accent);
    overflow: hidden;
    height: 100%;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
}

.state-header {
    padding: 12px 20px;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
}

.state-header.before, .state-header.after { 
    background: var(--extranet-primary); 
    color: white; 
}

.state-body {
    padding: 0;
    max-height: 500px;
    overflow-y: auto;
    flex: 1;
}

.custom-json-pretty {
    background-color: var(--extranet-secondary) !important;
    padding: 16px !important;
}

:deep(.vjs-tree) {
    font-family: 'JetBrains Mono', monospace !important;
    font-size: 13px !important;
}

:deep(.vjs-key) {
    color: #38bdf8 !important; /* Sky 400 */
}

:deep(.vjs-value__string) {
    color: #4ade80 !important; /* Green 400 */
}

:deep(.vjs-value__number) {
    color: #fbbf24 !important; /* Amber 400 */
}

:deep(.vjs-value__boolean) {
    color: #f472b6 !important; /* Pink 400 */
}

:deep(.vjs-colon), :deep(.vjs-brace), :deep(.vjs-bracket) {
    color: #94a3b8 !important; /* Slate 400 */
}

:deep(.vjs-ellipsis) {
    color: var(--extranet-primary) !important;
    font-weight: 800;
}

:deep(.vjs-carets) {
    color: #94a3b8 !important;
}

:deep(.vjs-carets svg) {
    color: #94a3b8 !important;
    fill: #94a3b8 !important;
}

:deep(.vjs-carets:hover) {
    color: var(--extranet-primary) !important;
}

:deep(.vjs-tree-brackets) {
    color: #94a3b8 !important;
}

:deep(.vjs-tree-brackets:hover) {
    color: var(--extranet-primary) !important;
}

:deep(.vjs-tree-node.dark:hover) {
    background-color: rgba(255,255,255,0.06) !important;
    border-radius: 4px !important;
}

.state-body.empty {
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fdfdfd;
}

.empty-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #94a3b8;
    font-size: 13px;
    font-weight: 500;
}

.empty-placeholder i {
    font-size: 24px;
    opacity: 0.5;
}

.code-block {
    margin: 0;
    padding: 20px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    line-height: 1.6;
    color: #334155;
    white-space: pre-wrap;
}

/* Pagination Footer */
.card-footer {
    padding: 24px 32px;
    background: #ffffff;
    border-top: 1px solid #f1f5f9;
}

/* Loading/Empty State */
.loading-state, .empty-list {
    padding: 100px 32px;
    text-align: center;
}

.empty-art {
    font-size: 48px;
    color: #e2e8f0;
    margin-bottom: 20px;
}

.empty-list h3 { font-size: 18px; font-weight: 700; color: #1e293b; margin-bottom: 8px; }
.empty-list p { color: #94a3b8; font-size: 14px; }

/* Responsive */
@media (max-width: 1200px) {
    .filter-bar { flex-direction: column; align-items: stretch; }
    .filter-left { flex-direction: column; align-items: stretch; }
    .date-input-wrapper { width: 100%; justify-content: space-between; }
}

@media (max-width: 768px) {
    .header-content { flex-direction: column; align-items: flex-start; gap: 20px; }
    .header-right { width: 100%; }
    .header-right .btn { width: 100%; justify-content: center; }
}
</style>
